import React, { useState, useRef, useEffect } from 'react';

export const SUPPORTED_SOCIALS = ['instagram', 'tiktok', 'spotify', 'linkedin', 'facebook', 'strava', 'youtube', 'x', 'snapchat', 'substack'] as const;

const ALL_ICON_TYPES = ['link', 'globe', ...SUPPORTED_SOCIALS] as const;

const ROOT_IMAGE = '/static/image/socials/icon__';

export type SupportedSocial = (typeof SUPPORTED_SOCIALS)[number];

export const linkPlatform = (link: string): SupportedSocial | null => {
    let host: string;
    try {
        host = new URL(/^https?:\/\//i.test(link) ? link : `https://${link}`).hostname.replace(/^www\./, '');
    } catch {
        return null;
    }
    return SUPPORTED_SOCIALS.find((name) => (
        host === `${name}.com` || host.endsWith(`.${name}.com`) || host === `${name}.app.link`
    )) ?? null;
};

export const determineLinkType = (link: string): string => linkPlatform(link) ?? 'link';

export const determineLinkSvg = (link: string) => {
    return `${ROOT_IMAGE}${determineLinkType(link)}.svg`;
};

export const iconTypeToSvg = (iconType: string) => {
    return `${ROOT_IMAGE}${iconType}.svg`;
};

interface SmartLinkInputProps {
    value: string;
    onChange: (value: string) => void;
    iconType?: string;
    onIconTypeChange?: (iconType: string | undefined) => void;
}

export default function SmartLinkInput({ value, onChange, iconType, onIconTypeChange }: SmartLinkInputProps) {
    const [showPicker, setShowPicker] = useState(false);
    const pickerRef = useRef<HTMLDivElement>(null);

    const activeType = iconType ?? determineLinkType(value);
    const iconSrc = `${ROOT_IMAGE}${activeType}.svg`;

    useEffect(() => {
        if (!showPicker) {return;}
        const handleClickOutside = (e: MouseEvent) => {
            if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
                setShowPicker(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showPicker]);

    const handleUrlChange = (newValue: string) => {
        onChange(newValue);
        if (!iconType) {return;}
        const autoDetected = determineLinkType(newValue);
        if (autoDetected === iconType) {
            onIconTypeChange?.(undefined);
        }
    };

    const handleIconSelect = (type: string) => {
        const autoDetected = determineLinkType(value);
        if (type === autoDetected) {
            onIconTypeChange?.(undefined);
        } else {
            onIconTypeChange?.(type);
        }
        setShowPicker(false);
    };

    return (
        <div className='smart-link__row'>
            <div className='smart-link__icon-wrapper' ref={pickerRef}>
                <button
                    type='button'
                    className='smart-link__icon-btn'
                    onClick={() => setShowPicker(!showPicker)}
                    title='Change icon'
                >
                    <img src={iconSrc} alt="Link" className="link__icon" />
                    <span className='smart-link__icon-caret'>&#9662;</span>
                </button>
                {showPicker && (
                    <div className='smart-link__icon-picker'>
                        {ALL_ICON_TYPES.map(type => (
                            <button
                                key={type}
                                type='button'
                                className={`smart-link__icon-option ${type === activeType ? 'smart-link__icon-option--active' : ''}`}
                                onClick={() => handleIconSelect(type)}
                                title={type}
                            >
                                <img src={`${ROOT_IMAGE}${type}.svg`} alt={type} />
                            </button>
                        ))}
                    </div>
                )}
            </div>
            <input
                className='smart-link__input'
                type='text'
                placeholder='https://...'
                value={value}
                onChange={(e) => handleUrlChange(e.target.value)}
            />
        </div>
    );
}
